import Firebase from 'firebase/app'
import 'firebase/firestore'
import axios from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import snakeCase from 'lodash/snakeCase'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'

import { mapGetters, mapState } from 'vuex'
import { get, set } from 'dot-prop'

import { FLOWS_COLLECTION } from '@/constants'
import getThumbnailUrl from '@/components/form/editor/helpers/thumbnailUrl'
import { replaceDuplicateIds } from '@/components/form/editor/helpers/formHelpers'
import id from '@/components/form/editor/helpers/id'
import { notifySavvy } from '../../helpers/notify'
import googleFontsUrls from '../../helpers/googleFontsUrls'

const MAX_FORM_LENGTH = 800000

export default {
  props: { id: String },
  provide() {
    return {
      _updateReactive: this.updateReactive,
    }
  },
  inject: {
    _register: { default: () => () => {} },
  },
  data() {
    return {
      savedForm: null,
      form: null,
      tempForm: null,
      unsavedChanges: [],
      formFreezer: null,
      coldUpdates: false,
      forceResetDuration: 0,
      fonts: [],
    }
  },
  computed: {
    ...mapState(['highestVersion', 'versionData', 'allFlows']),
    ...mapGetters(['activeGroupId', 'isAdmin', 'userId']),
    version() {
      return this.versionData && this.versionData.version
    },
    unsaved: {
      get() {
        return this.$store.state.hasUnsavedFlowChanges
      },
      set(v) {
        this.$store.commit('setHasUnsavedFlowChanges', v)
        // if (v) {
        //   window.addEventListener('beforeunload', this.unsavedWarningListener)
        // } else {
        //   window.removeEventListener('beforeunload', this.unsavedWarningListener)
        // }
      },
    },
    saving: {
      get() {
        return this.$store.state.isSavingFlowChanges
      },
      set(v) {
        this.$store.commit('setIsSavingFlowChanges', v)
      },
    },
    hasChanges() {
      return !isEqual(this.form, this.formFreezer)
    },
    unsavedChangesDocId() {
      const currentVersion = this.versionData && this.versionData.version
      return `unsaved-version-${currentVersion}-${this.userId}`
    },
    debounceUpdateHistory() {
      return debounce(this.updateHistory, 1000, { trailing: true, leading: false })
    },
    debounceLoadFlow() {
      return debounce(this.loadForm, 500, { trailing: true, leading: false })
    },
  },
  mounted() {
    // this.unsavedWarningListener = function(e) {
    //   var confirmationMessage =
    //     'You have unsaved changes to this flow! ' + 'Are you sure you want to leave this page?'

    //   ;(e || window.event).returnValue = confirmationMessage //Gecko + IE
    //   return confirmationMessage //Gecko + Webkit, Safari, Chrome etc.
    // }
    if (this.deregistersave) this.deregistersave()
    if (this.deregisterdiscard) this.deregisterdiscard()
    this.deregistersave = this._register('save', this.save)
    this.deregisterdiscard = this._register('discard', this.discard)
  },
  beforeDestroy() {
    // if (this.unsavedWarningListener) {
    //   window.removeEventListener('beforeunload', this.unsavedWarningListener)
    // }
    if (this.deregistersave) this.deregistersave()
    if (this.deregisterdiscard) this.deregisterdiscard()
  },
  watch: {
    id: {
      async handler(id) {
        if (this.$store.getters.userId) {
          const userRef = Firebase.firestore()
            .collection('users')
            .doc(this.$store.getters.userId)
          const userDoc = await userRef.get()
          const recentFlows = userDoc.get('recentFlows') || []
          userRef.update({ recentFlows: [id, ...recentFlows.filter(f => f !== id)] })
        }
      },
      immediate: true,
    },
    version: {
      async handler(v) {
        this.debounceLoadFlow(this.id, v)
      },
      immediate: true,
    },
    form: {
      handler(form) {
        this.onEditorFormUpdate(form)
        this.updateUnsaved()
        this.replaceOptionButtons()
      },
      deep: true,
    },
    savedForm: {
      handler(v, o) {
        if (isEqual(v, o)) return
        const newFonts = []
        const oldFonts = []
        if (v) {
          this.form = replaceDuplicateIds(v)
          this.loadUnsavedChanges()
          newFonts.push(...googleFontsUrls(v.styles || {}))
        }
        if (o) {
          const seenNewFonts = new Set(newFonts)
          oldFonts.push(...googleFontsUrls(o.styles || {}).filter(f => !seenNewFonts.has(f)))
        }
        this.$store.commit('setNewFont', newFonts, oldFonts)

        this.updateUnsaved()
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    // async loadForm(id, version) {
    //   const baseRef = Firebase.firestore()
    //     .collection(FLOWS_COLLECTION)
    //     .doc(id)
    //   const ref =
    //     typeof version === 'number'
    //       ? baseRef.collection('history').doc(`version-${version}`)
    //       : baseRef
    //   let doc = await ref.get()
    //   if (!doc.exists) {
    //     const docs = await baseRef
    //       .collection('history')
    //       .where('version', '==', version)
    //       .limit(1)
    //       .get()
    //     doc = docs.docs[0]
    //   }
    //   await this.$bind('unsavedChanges', baseRef.collection('history').where('saved', '==', false))
    //   const form = JSON.parse(doc.get('form') || '{}')
    //   this.savedForm = form
    // },
    updateReactive(path, key, value) {
      const pathIsArray = Array.isArray(path)
      if (!pathIsArray && path && path.startsWith('.')) path = path.slice(1)

      const isResponsive = null
      const fullPath = [isResponsive, ...(pathIsArray ? path : [path]), key]
        .filterExists()
        .join('.')

      const form = cloneDeep(this.form)
      if (pathIsArray) {
        const partialPath = [isResponsive].filterExists().join('.')
        const startObj = get(form, partialPath, partialPath === '' ? form : {})
        let obj = startObj
        path.forEach((k, i) => {
          obj[k] = obj[k] || (typeof path[i + 1] === 'number' ? [] : {})
          obj = obj[k]
        })
        if (value === null) delete obj[key]
        else obj[key] = value

        if (partialPath !== '') set(form, partialPath, startObj)
      } else {
        if (value === null) {
          const val = get(
            form,
            [isResponsive, ...(pathIsArray ? path : [path])].filterExists().join('.')
          )
          delete val[key]
        } else {
          set(form, fullPath, value)
        }
      }

      const splitPath = fullPath.split('.')
      const lastKey = splitPath.slice(-1).join('.')
      const mostPath = splitPath.slice(0, -1).join('.')

      const updateObj = get(this.form, mostPath)
      const newVal = get(form, fullPath)
      if (updateObj && newVal) {
        this.$set(updateObj, lastKey, newVal)
      } else {
        let firstKey = fullPath.split('.')[0]
        this.$set(this.form, firstKey, form[firstKey])
      }

      this.debounceUpdateHistory()
    },
    async save(status = 'saved') {
      if (this.saving) return
      this.saving = true
      const primaryProductId =
        (this.$store.state.products[0] && this.$store.state.products[0].id) || null
      const hasUnsaved = this.unsaved
      const currentVersion = this.versionData && this.versionData.version
      const newVersion = hasUnsaved ? this.highestVersion + 1 : currentVersion
      const userName =
        this.$store.getters.user.nickname ||
        (this.$store.getters.user.auth &&
          (this.$store.getters.user.auth.displayName || this.$store.getters.user.auth.email))
      const author = { userId: this.$store.getters.userId, userName: userName || 'Unnamed User' }
      const form = cloneDeep(this.form || {})

      if (form.pages) form.pages = this.syncOptionButtonText(form)
      form.id = this.id
      const masterFlow = this.allFlows.find(f => f.id === this.id)
      const masterGroupId = masterFlow && masterFlow._meta && masterFlow._meta.groupId
      const groupId = masterGroupId || this.activeGroupId
      // const masterTitle = masterFlow && masterFlow._meta && masterFlow._meta.title
      // if (masterTitle) form.title = masterTitle
      delete form._meta

      const batch = Firebase.firestore().batch()
      const baseRef = Firebase.firestore()
        .collection(FLOWS_COLLECTION)
        .doc(this.id)

      /*
      If hasUnsaved: save current to status: 'saved' only
      else:
        if status = prod
        * Check current version doc and make sure status is set to staging, then set to prod
        if status = staging
        * Check current vesrion doc and make sure status is NOT prod
        if status = saved
        * wtf
      
      */

      batch.delete(baseRef.collection('history').doc(this.unsavedChangesDocId))
      batch.delete(baseRef.collection('history').doc(`unsaved-${author.userId}`))

      let formString = JSON.stringify(form || {})
      const tooLong = formString.length > MAX_FORM_LENGTH

      if (tooLong) alert(`Flow is a large size - saving in Savvy's New Storage System`)

      if (hasUnsaved) {
        batch.set(baseRef.collection('history').doc(`version-${newVersion}`), {
          groupId,
          form: tooLong ? null : formString,
          json_storage_location: form.preferred_json_storage_location || tooLong ? 'kv' : null,
          author: author,
          saved: true,
          status: 'saved',
          version: newVersion,
          from_version: currentVersion || null,
          updatedAt: Firebase.firestore.FieldValue.serverTimestamp(),
        })
        fetch(`https://flow-storage.heysavvy.workers.dev/flows/${form.id}@${newVersion}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: formString,
        })
      } else {
        const versionRef = baseRef.collection('history').doc(`version-${newVersion}`)
        const versionDoc = await versionRef.get()
        const versionGroupId = versionDoc.get('groupId')
        const versionFormData = versionDoc.get('form')
        const versionStatus = versionDoc.get('status')
        const storageLocation = versionDoc.get('json_storage_location')
        let canDeploy = this.isAdmin || this.$store.getters.allowPushLive // || isAllowed
        if (!canDeploy) {
          try {
            if (this.$warrant) {
              const isAllowed = await this.$warrant.hasWarrant('flow', 'general', 'editor')
              if (isAllowed) canDeploy = true
            }
          } catch (error) {
            console.error(error)
          }
        }
        const toProd = status === 'prod' && versionStatus === 'staging' && canDeploy
        const toStaging = status === 'staging' && versionStatus === 'saved'
        const allowed = toProd || toStaging

        if (toProd) {
          const savedFormData = {
            groupId: versionGroupId || groupId,
            form: versionFormData, //||JSON.stringify(form),
            json_storage_location: storageLocation || null,
            primaryProductId,
            version: newVersion,
            isLive: true,
            versionHistory: Firebase.firestore.FieldValue.arrayUnion(currentVersion),
          }
          // const versionFlow = JSON.parse(versionFormData)
          // if (versionFlow.title) savedFormData.title = versionFlow.title
          batch.set(baseRef, savedFormData, { merge: true })

          fetch(`https://flow-storage.heysavvy.workers.dev/flows/${form.id}`, {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
            },
            body: formString,
          })
        }

        if (allowed) {
          const docsToBeUpdated = await baseRef
            .collection('history')
            .where('status', '==', status)
            .get()
          docsToBeUpdated.docs.forEach(d => batch.update(d.ref, { status: 'saved' }))
          batch.set(
            versionRef,
            {
              status: status || 'saved',
              status_history: Firebase.firestore.FieldValue.arrayUnion({
                status: status || null,
                timestamp: Firebase.firestore.Timestamp.now(),
              }),
            },
            { merge: true }
          )
        } else {
          const errorMessage = `Save failed: Changing ${versionStatus} to ${status} is not allowed`
          console.error(errorMessage)
          this.$message.error(errorMessage)
          this.saving = false
          return
        }
      }

      batch
        .commit()
        .then(() => {
          // this.$message.success('Flow Saved!')
          const versionData = { version: newVersion, id: `version-${newVersion}` }
          this.$store.commit('setVersionData', versionData)
          if (this.reloadDefaults) this.reloadDefaults()
          try {
            axios.get(getThumbnailUrl(this.id, newVersion))
          } catch (error) {
            console.error(error.response.data)
          }

          const leapfrogged = currentVersion && newVersion - currentVersion > 1

          if (hasUnsaved && (!this.isAdmin || leapfrogged)) {
            this.notifyVersionUpdate(newVersion, author, currentVersion, leapfrogged)
          }
        })
        .catch(error => {
          this.$message.error('Error Saving Signup Form')
          console.error('Error Saving Signup Form:', error)
        })
        .finally(() => (this.saving = false))
    },
    discard() {
      this.form = cloneDeep(this.savedForm)
      this.removeRedundantUnsavedDoc()
      this.selectedComponentId = null
    },
    async notifyVersionUpdate(newVersion, author, oldVersion, lf) {
      const baseRef = Firebase.firestore()
        .collection(FLOWS_COLLECTION)
        .doc(this.id)
      const versionUpdateRef = baseRef.collection('updates').doc('version-updates')
      const versionUpdateDoc = await versionUpdateRef.get()
      const versionTimestamp = versionUpdateDoc.get('timestamp') || 0
      const options = {}
      const now = Date.now()
      if (now - versionTimestamp <= 30 * 60 * 1000) {
        options.thread = versionUpdateDoc.get('thread')
      }
      const thread = await notifySavvy(
        `${author.userName} has saved v${newVersion} of ${this.form.title}\n\nGroup Id: \`${
          this.activeGroupId
        }\`   |   Flow Id: \`${this.id}\` ${lf ? `   |   *Original Version: v${oldVersion}*` : ''}`,
        'C01R8K90KQA',
        options
      )
      await versionUpdateRef.set({ timestamp: now, thread: thread.ts || null })
    },
    updateUnsaved() {
      const unsaved = this.form && !isEqual(this.form, this.savedForm)
      this.unsaved = unsaved
    },
    async removeRedundantUnsavedDoc() {
      const unsavedDocId = this.unsavedChangesDocId
      const batch = Firebase.firestore().batch()
      const baseRef = Firebase.firestore()
        .collection(FLOWS_COLLECTION)
        .doc(this.id)

      const ref = baseRef.collection('history').doc(unsavedDocId)
      const legacyRef = baseRef.collection('history').doc(`unsaved-${this.$store.getters.userId}`)

      batch.delete(ref)
      batch.delete(legacyRef)

      await batch.commit()
    },
    updateHistory() {
      const version = this.versionData.version || this.highestVersion
      const userName =
        this.$store.getters.user.nickname ||
        (this.$store.getters.user.auth &&
          (this.$store.getters.user.auth.displayName || this.$store.getters.user.auth.email)) ||
        'Unnamed User'
      const author = { userId: this.$store.getters.userId, userName }

      const data = {
        groupId: this.activeGroupId,
        form: JSON.stringify(this.form || {}),
        author,
        saved: false,
        version,
        updatedAt: Firebase.firestore.FieldValue.serverTimestamp(),
      }

      const unsaved = this.form && !isEqual(this.form, this.savedForm)

      if (unsaved) {
        Firebase.firestore()
          .collection(FLOWS_COLLECTION)
          .doc(this.id)
          .collection('history')
          .doc(this.unsavedChangesDocId)
          .set(data)
      } else {
        this.removeRedundantUnsavedDoc()
      }
    },
    syncOptionButtonText(form) {
      const pages = form.pages.map(p => {
        const components = p.components
        return {
          ...p,
          id: p.id || id(),
          components:
            components &&
            components.map(c =>
              c.type === 'OptionButtons' || c.type === 'OptionSelector'
                ? {
                    ...c,
                    id: c.id || id(),
                    buttons:
                      c.buttons &&
                      c.buttons.map(b => {
                        if (!b.key) {
                          this.$message.info(
                            `Key auto-generated for OptionSelector with text "${b.text}" on page ${p.key}`,
                            5
                          )
                        }
                        return {
                          ...b,
                          key: b.key || (typeof b.text === 'string' && snakeCase(b.text.trim())),
                        }
                      }),
                  }
                : c.id
                ? c
                : { ...c, id: c.id || id() }
            ),
        }
      })
      return pages
    },
    loadUnsavedChanges() {
      // const doc = this.unsavedChanges.find(d => d.id === `unsaved-${this.$store.getters.userId}`)
      const docs = this.unsavedChanges.filter(
        d => d.id === this.unsavedChangesDocId || d.id === `unsaved-${this.$store.getters.userId}`
      )
      const doc = docs[0]

      if (doc && this.versionData && this.versionData.version === doc.version) {
        // this.highestVersion = (doc.version && doc.version - 1) || 0
        this.form = replaceDuplicateIds({
          id: doc.id,
          pages: [],
          ...JSON.parse(doc.form || `{ "id": "${this.id}", "pages": [] }`),
        })
        this.$emit('version-select', { id: `version-${doc.version}`, version: doc.version })
        this.$message.info(`Loaded unsaved changes from version ${doc.version}`)
        return true
      }
      return false
    },
    replaceOptionButtons() {
      const stringForm = JSON.stringify((this.form && this.form.pages) || [])
      if (stringForm.includes('"type":"OptionButtons"')) {
        const pages = JSON.parse(
          stringForm.split(`"type":"OptionButtons"`).join(`"type":"OptionSelector"`)
        )
        if (isEqual(pages, this.form.pages)) return
        this.updateReactive(null, 'pages', pages)
        // this.form = form
        this.$message.info(
          'Auto-replaced component types containing "OptionButtons" with "OptionSelector"'
        )
      }
    },
    onVersionSelect(versionData) {
      this.$store.commit('setVersionData', versionData)
    },
    setFreezeStatus() {
      const status = !this.coldUpdates
      /* Status = true, form is frozen */
      if (status === false) this.loadFrozenForm()
      this.coldUpdates = status

      this.formFreezer = status ? this.tempForm : null
    },
    loadFrozenForm() {
      this.tempForm = this.formFreezer
    },
    onUpdateForm([k, v]) {
      this.updateReactive(null, k, v)
    },
    onEditorFormUpdate(e) {
      if (this.coldUpdates) {
        this.formFreezer = cloneDeep(e)
        this.forceResetDuration += 1
      } else this.tempForm = e
    },
  },
}
